import { useRef } from "react";
import { Link } from "react-router-dom";
import { colors } from "../../style";
import { Icon } from "./Icon";

const sizes = {
  md: { padding: "7px 14px", borderRadius: "9px" },
  sm: { padding: "6px 12px", borderRadius: "8px" },
};

const containers = {
  segmented: {
    display: "inline-flex",
    alignSelf: "flex-start",
    gap: "4px",
    padding: "4px",
    borderRadius: "12px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
  },
  plain: { display: "flex", gap: "4px" },
  pills: { display: "flex", gap: "8px", flexWrap: "wrap" },
};

const tabStyle = (variant, size, on, disabled) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: 0,
  ...sizes[size],
  background: on ? colors.raised : "none",
  color: on ? colors.text : colors.textMuted,
  // Segmented takes the parent's line-height too (Semantic sets buttons to 1.15)
  ...(variant === "segmented"
    ? { font: "inherit" }
    : { fontFamily: "inherit" }),
  fontSize: "13px",
  fontWeight: 600,
  textDecoration: "none",
  whiteSpace: "nowrap",
  cursor: disabled ? "default" : "pointer",
  opacity: disabled ? 0.5 : 1,
  ...(variant === "pills" && {
    border: `1px solid ${on ? "transparent" : colors.borderStrong}`,
    borderRadius: "999px",
    padding: size === "sm" ? "4px 12px" : "6px 14px",
    color: on ? colors.text : "rgba(255, 255, 255, 0.75)",
  }),
});

const badgeStyle = {
  padding: "0 6px",
  borderRadius: "999px",
  background: colors.borderStrong,
  fontSize: "11px",
  lineHeight: "16px",
};

const NEXT = { ArrowRight: 1, ArrowLeft: -1 };

// Every tab bar, segmented switch and filter row. Items with `to` render as
// router links (route tabs). Arrows/Home/End move focus; Enter, Space or a
// click selects, unless activation="auto".
export function Tabs({
  items,
  value,
  onChange,
  variant = "segmented",
  size = "md",
  activation = "manual",
  ariaLabel,
  style,
}) {
  const refs = useRef([]);
  // Roving tabindex; first enabled tab if the selected one is missing or disabled
  const stop = items.some((item) => item.value === value && !item.disabled)
    ? value
    : items.find((item) => !item.disabled)?.value;

  const onKeyDown = (e) => {
    const enabled = items
      .map((item, i) => (item.disabled ? -1 : i))
      .filter((i) => i >= 0);
    if (!enabled.length) return;
    const focused = refs.current.indexOf(document.activeElement);
    // Links only activate on Enter natively
    if (e.key === " " && items[focused]?.to && !items[focused].disabled) {
      e.preventDefault();
      refs.current[focused].click();
      return;
    }
    const at = enabled.indexOf(focused);
    let next;
    if (e.key in NEXT) {
      next =
        enabled[
          (Math.max(at, 0) + NEXT[e.key] + enabled.length) % enabled.length
        ];
    } else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    else return;
    e.preventDefault();
    refs.current[next]?.focus();
    const item = items[next];
    if (activation === "auto" && !item.to) onChange?.(item.value);
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKeyDown}
      style={{ ...containers[variant], ...style }}
    >
      {items.map((item, i) => {
        const on = item.value === value;
        const props = {
          ref: (el) => (refs.current[i] = el),
          role: "tab",
          "aria-selected": on,
          tabIndex: item.value === stop ? 0 : -1,
          className: "sk-tab",
          style: tabStyle(variant, size, on, item.disabled),
        };
        const body = (
          <>
            {item.icon && <Icon name={item.icon} size={14} />}
            {item.label}
            {item.badge != null && <span style={badgeStyle}>{item.badge}</span>}
          </>
        );
        if (item.to) {
          return item.disabled ? (
            <span key={item.value} {...props} aria-disabled="true">
              {body}
            </span>
          ) : (
            <Link
              key={item.value}
              {...props}
              to={item.to}
              onClick={() => onChange?.(item.value)}
            >
              {body}
            </Link>
          );
        }
        return (
          <button
            key={item.value}
            {...props}
            type="button"
            disabled={item.disabled}
            onClick={() => onChange?.(item.value)}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}
